import FolderIcon from "@mui/icons-material/FolderShared";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";

// styling
import Paper from "@mui/material/Paper";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Link, useLocation, useMatch } from "react-router-dom";
import { ROUTES } from "../../App/routePaths";
import TicketChatContainer from "../../containers/TicketDetailContainer/TicketChatContainer";
import { ticketIsPrivate } from "../../functions/ticketIsPrivate";
import { ROLES } from "../../modules/accounts/accountConstants";
import { accountHasRole } from "../../modules/accounts/accountFunctions";
import { accountSelectors } from "../../modules/accounts/accountSlice";
import { currentTicketSelectors } from "../../modules/ticket/ticketDetail/currentTicket/currentTicketSlice";
import AnnouncementIconWithBadge from "./AnnouncementIconWithBadge";

// intl
const messages = {
  home: {
    id: "mainNavigation.home",
    defaultMessage: "Home",
  },
  tickets: {
    id: "mainNavigation.tickets",
    defaultMessage: "Notifications",
  },
  myPropertyManagement: {
    id: "mainNavigation.myPropertyManagement",
    defaultMessage: "My PM",
  },
  profile: {
    id: "mainNavigation.profile",
    defaultMessage: "Profile",
  },
};

function MainNavigation() {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const currentTicket = useSelector(currentTicketSelectors.selectCurrentTicket);
  const currentAccount = useSelector(accountSelectors.selectCurrentAccount);
  const match = useMatch("/tickets/:ticketId");

  const value = location.pathname;

  if (
    isDetailViewAndMessageEnabled({
      match,
      currentTicket,
      currentAccount,
    })
  ) {
    return <TicketChatContainer />;
  }

  return (
    <Paper
      style={{ position: "fixed", bottom: 0, left: 0, right: 0 }}
      elevation={3}
    >
      <BottomNavigation value={value} showLabels>
        <BottomNavigationAction
          component={Link}
          label={formatMessage(messages.home)}
          icon={<HomeIcon />}
          // @ts-expect-error inherited from react-router-dom link
          to={ROUTES.HOME}
          value={ROUTES.HOME}
        />

        <BottomNavigationAction
          component={Link}
          label={formatMessage(messages.tickets)}
          icon={<AnnouncementIconWithBadge />}
          // @ts-expect-error inherited from react-router-dom link
          to={ROUTES.TICKET_LIST}
          value={ROUTES.TICKET_LIST}
        />

        <BottomNavigationAction
          component={Link}
          label={formatMessage(messages.myPropertyManagement)}
          icon={<FolderIcon />}
          // @ts-expect-error inherited from react-router-dom link
          to={ROUTES.DOCUMENTS}
          value={ROUTES.DOCUMENTS}
        />

        <BottomNavigationAction
          component={Link}
          label={formatMessage(messages.profile)}
          icon={<PersonIcon />}
          // @ts-expect-error inherited from react-router-dom link
          to={ROUTES.PROFILE}
          value={ROUTES.PROFILE}
        />
      </BottomNavigation>
    </Paper>
  );
}

/**
 * Determines whether the chat input should be shown instead of the bottom navigation.
 * The chat form is displayed when the user is viewing a ticket detail and either:
 * - the ticket is private, or
 * - the user has the TICKET_MANAGER role.
 */
const isDetailViewAndMessageEnabled = ({
  match,
  currentTicket,
  currentAccount,
}) => {
  if (!match?.params?.ticketId) {
    return false;
  }

  const ticketId = match.params.ticketId;

  // Note: currentTicket.id comes from the API as number, ticketId from route params is always a string.
  // String() ensures a reliable comparison until the Ticket type is corrected to id: number.
  if (!currentTicket?.id || String(currentTicket.id) !== ticketId) {
    return false;
  }

  const userIsTicketManager = accountHasRole(
    currentAccount,
    ROLES.TICKET_MANAGER,
  );

  const isPrivate = ticketIsPrivate(currentTicket);

  // except for ticket managers, only the ticket creator and the estate can see private messages
  return isPrivate || userIsTicketManager;
};

export default MainNavigation;
